import { of, Subscription } from 'rxjs';
import { first, switchMap, take } from 'rxjs/operators';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { AppInviteData } from 'src/lib/fliko/models/appInvite.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { NoteType } from 'src/lib/fliko/models/notes/notes.data';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ResolutionVote } from 'src/lib/fliko/models/resolutions/resolution-enum';
import { RentalUnit, RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { UserData } from 'src/lib/fliko/models/users/user.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { SnackBarService } from 'src/app/shared/services/snackBar.service';
import { CompanyService } from 'src/app/view-company/service/company.service';
import {
  getConnectionDisplayStatus,
  getConnectionTone
} from 'src/app/view/connections/components/users-resident-connections/users-resident-connections.models';
import { ConnectionsDataService } from 'src/app/view/connections/services/connections-data.service';
import { ResidentConnectionVM } from 'src/app/view/connections/services/models/connections.view-model';
import { ResolutionVoteEx } from 'src/app/view/resolutions/service/models/voteTable-view-models';
import { ResolutionService } from 'src/app/view/resolutions/service/resolution.service';
import { ResolutionDataService } from 'src/app/view/resolutions/service/resolutions-data.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ConnectionActiveCallOverlayService } from '../../../services/connection-active-call-overlay.service';
import { UserCommList } from '../../../services/models/users-table.data';
import { UnitCommResidencyViewModel } from '../../../services/models/users-view-models';
import { ThreeCxService } from '../../../services/three-cx.service';
import { UserService } from '../../../services/user.service';
import { UsersCommDataService } from '../../../services/users-comm-data.service';

class ResolutionVoteViewModel {
  resId = '';
  resStatus = '';
  vote = '';
  name = '';
}

@Component({
  selector: 'app-users-comm-resident-details',
  templateUrl: './users-comm-resident-details.component.html',
  styleUrls: ['./users-comm-resident-details.component.scss']
})
export class UsersCommResidentDetailsComponent implements OnInit, OnDestroy {
  public userData!: UserData;
  public residentRecentConnections: ResidentConnectionVM[] = [];

  public yesVotesVMs: ResolutionVoteViewModel[] = [];
  public noVotesVMs: ResolutionVoteViewModel[] = [];
  public holdVotesVMs: ResolutionVoteViewModel[] = [];
  public mixedVotesVMs: ResolutionVoteViewModel[] = [];

  public aptResidencyVMs: UnitCommResidencyViewModel[] = [];
  public parkResidencyVMs: UnitCommResidencyViewModel[] = [];
  public stoResidencyVMs: UnitCommResidencyViewModel[] = [];
  public allUnits: RentalUnit[];
  // ? tenant tenancy history
  public previousTenanciesVMs: UnitCommResidencyViewModel[] = [];

  public isAppInviteSent = false;
  public invitationData!: AppInviteData;
  public showTenantsEdit = false;
  public showResolutions = false;
  public canEdit = false;
  public canAuth = false;
  public canMessage = false;
  public canAlert = false;
  public canCall3cx = false;
  public calling3cxPhone: string | null = null;

  public showOwnerCodes = false;

  public get anyBasicDataExist(): boolean {
    // eslint-disable-next-line max-len
    const basicDataKeys = ['birthDate', 'PESEL', 'documentNo', 'nationality', 'NIP', 'REGON', 'ownerCode', 'address', 'creatorUserId']; // prettier-ignore
    return ObjHlp.haveAny(this.data.resident, basicDataKeys);
  }

  public get typeHR(): string {
    return GrammarHelper.getResidentInCase(EstateType.COMMUNITY, this.data.type, 'M', this.ls);
  }

  public get showAllResidentConnectionsText(): string {
    return this.ls.translate('connection.showAllResidentCalls');
  }

  public get showLessResidentConnectionsText(): string {
    return this.ls.translate('connection.showLessResidentCalls');
  }

  private isComm: boolean = false;

  private subscription$: Subscription = new Subscription();

  RentalUnitType = RentalUnitType;
  ResidentType = ResidentType;
  NoteType = NoteType;
  VoteType = ResolutionVote;
  getResidentConnectionStatus = getConnectionDisplayStatus;
  getResidentConnectionToneForConnection = getConnectionTone;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UserCommList.ResidentElement,
    private dialogRef: MatDialogRef<UsersCommResidentDetailsComponent>,
    private ctxDS: ContextDataService,
    private resolutionService: ResolutionService,
    private resolutionDataService: ResolutionDataService,
    private router: Router,
    private userService: UserService,
    private connectionActiveCallOverlayService: ConnectionActiveCallOverlayService,
    private connectionsDataService: ConnectionsDataService,
    private userCommDataService: UsersCommDataService,
    private errorHandlingService: ErrorHandlingService,
    private companyService: CompanyService,
    private threeCxService: ThreeCxService,
    private snackBarService: SnackBarService,
    private ls: LocalizationService
  ) {
    this.allUnits = [...data.apartments, ...data.parkings, ...data.storages];
  }

  ngOnInit(): void {
    this.loadResidentRecentConnections();
    this.subscription$.add(
      this.ctxDS.contextPerms$.subscribe((perms) => {
        const isSuperAdmin = !!perms?.superAdmin;
        const p = perms?.ePerm.user;
        const web = perms?.feat?.web;

        this.canEdit = isSuperAdmin || !!p?.edit;
        this.canAuth = isSuperAdmin || !!p?.auth;
        this.canMessage = !!web?.messaging && (isSuperAdmin || !!perms?.ePerm?.chat?.view);
        this.canAlert = !!web?.alertSMS?.enabled && (isSuperAdmin || !!perms?.ePerm.news?.edit);
        this.showTenantsEdit = perms?.feat?.mobile?.communityTenants ?? false;

        this.isComm = !perms?.isPrs;
      })
    );
    this.subscription$.add(
      this.ctxDS.currentUser$
        .pipe(switchMap((user) => (user ? this.companyService.getEmployee$(user.id) : of(null))))
        .subscribe((employee) => {
          this.canCall3cx = !!(employee?.['3cxNumberSan'] || employee?.['3cxNumber']);
        })
    );

    this.getResidentsInUnits();
    if (this.data.type === ResidentType.LANDLORD) this.prepareVotesVMs();
    this.getUserData();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private loadResidentRecentConnections(): void {
    const residentId = this.data.resident.id;

    this.subscription$.add(
      this.connectionsDataService.getConnections$().subscribe((connections) => {
        this.residentRecentConnections = connections.filter((connection) => connection.residentId === residentId);
      })
    );
  }

  private getUserData(): void {
    const userId = this.data.resident.userId;
    if (userId) {
      this.subscription$.add(
        this.userService
          .getUser$(userId)
          .pipe(first((x) => !!x))
          .subscribe((user) => {
            if (user) {
              this.userData = user;
            } else {
              this.errorHandlingService.showGlobalError(this.ls.translate('error.userFound'));
            }
          })
      );
    }
  }

  private getResidentsInUnits(): void {
    // TODO: show archive tenancies; resident element do not have archive units
    // prettier-ignore
    this.subscription$.add(
      this.userCommDataService
        // eslint-disable-next-line max-len
        .getUnitsResidencyData$(RentalUnitType.APARTMENT, [...this.data.apartments, ...this.data.archiveApartments].map((a) => a.id))
        .subscribe((apVM) => (this.aptResidencyVMs = this.handleTenancies(apVM)))
    );
    // prettier-ignore
    this.subscription$.add(
      this.userCommDataService
        .getUnitsResidencyData$(RentalUnitType.PARKING, [...this.data.parkings, ...this.data.archiveParkings].map((p) => p.id))
        .subscribe((pVM) => (this.parkResidencyVMs = this.handleTenancies(pVM)))
    );
    // prettier-ignore
    this.subscription$.add(
      this.userCommDataService
        .getUnitsResidencyData$(RentalUnitType.STORAGE, [...this.data.storages, ...this.data.archiveStorages].map((s) => s.id))
        .subscribe((sVM) => (this.stoResidencyVMs = this.handleTenancies(sVM)))
    );
  }

  private handleTenancies(data: UnitCommResidencyViewModel[]): UnitCommResidencyViewModel[] {
    if (this.isComm && this.data.type === ResidentType.TENANT) {
      const ret: UnitCommResidencyViewModel[] = [];
      data.forEach((residency) => {
        const thisResident = residency.residents.find((r) => r.id === this.data.id);
        if (thisResident) {
          if (thisResident.currentTenancies?.length) ret.push(residency);
          if (thisResident.previousTenancies?.length) this.previousTenanciesVMs.push(residency);
        }
      });
      return ret;
    } else {
      return data;
    }
  }

  private prepareVotesVMs(): void {
    this.subscription$.add(
      this.resolutionDataService.getResidentVotingData$(this.data?.resident.id, false).subscribe((voteData) => {
        this.yesVotesVMs = [];
        this.noVotesVMs = [];
        this.holdVotesVMs = [];
        this.mixedVotesVMs = [];
        const resVotes = {
          yes: new Set<string>(),
          no: new Set<string>(),
          hold: new Set<string>(),
          none: new Set<string | undefined>()
        };

        voteData.voted.forEach((v) => resVotes[v.data.vote].add(v.data.resolutionId));
        voteData.possible.forEach((v) => resVotes.none.add(v.resolution?.id)); // ? none + y/n/h => mixed

        this.resolutionService
          .getResolutions$()
          .pipe(take(1))
          .subscribe((resolutions) => {
            resolutions.forEach((res) => {
              const votes: ResolutionVoteEx[] = [];
              for (const vote in resVotes) {
                if ((resVotes as any)[vote].has(res.id)) votes.push(vote as ResolutionVoteEx);
              }

              let voteValue: ResolutionVoteEx = 'none';
              if (votes.length > 1) voteValue = 'mixed';
              else if (votes.length) voteValue = votes[0];

              if (voteValue !== 'none') {
                (this as any)[voteValue + 'VotesVMs'].push({
                  resId: res.id,
                  resStatus: res.status,
                  name: res.name,
                  vote: voteValue
                });
                this.showResolutions = true;
              }
            });
          });
      })
    );
  }

  public onEdit(): void {
    if (this.data.type === ResidentType.LANDLORD) this.onLandlordEdit();
    else this.onTenantEdit();
  }

  public onLandlordEdit(): void {
    if (!this.canEdit) return;
    this.router.navigate(['/users/edit', this.data?.resident?.id]);
    this.dialogRef.close();
  }

  public onTenantEdit(): void {
    if (!this.canEdit) return;
    this.router.navigate(['/users/tenant/edit', this.data?.resident?.id]);
    this.dialogRef.close();
  }

  public routeToResolution(id: string, status: string): void {
    const type = status === 'accepted' || status === 'rejected' ? 'ended' : status;
    this.router.navigate(['/resolutions', type, id]);
    this.dialogRef.close();
  }

  public openActiveCallOverlay(connection: ResidentConnectionVM, event?: Event): void {
    event?.stopPropagation();
    this.connectionActiveCallOverlayService.open(connection);
  }

  public async make3cxCall(phone: string): Promise<void> {
    if (!this.canCall3cx || this.calling3cxPhone) return;

    this.calling3cxPhone = phone;
    try {
      const response = await this.threeCxService.makeCall({
        estateId: this.data.resident.estateId,
        residentId: this.data.resident.id,
        destination: phone
      });
      this.snackBarService.showSuccess('success.make3cxCall');
      await this.connectionActiveCallOverlayService.openCall(response.connectionId);
    } catch (error) {
      this.errorHandlingService.showGlobalError('error.make3cxCall', error);
    } finally {
      this.calling3cxPhone = null;
    }
  }

  public unitDocsPrefix(index: number): string {
    const t = (x: string) => this.ls.translate(x);
    let ret = `${t('label.for')} `;
    if (index < this.data.apartments.length) {
      ret += `${t('commApartment.genitive')}:`;
    } else if (index < this.data.apartments.length + this.data.parkings.length) {
      ret += `${t('parking.genitive')}:`;
    } else {
      ret += `${t('storage.genitive')}:`;
    }
    return ret;
  }
}
